import type { Id } from "@repo/convex/dataModel";
import { Clock3, Sparkles } from "lucide-react";
import { useMemo } from "react";
import { CitationChip } from "@/components/ui/citation-chip";
import { MessageMarkdown } from "@/components/ui/message-markdown";
import { cn } from "@/lib/utils";
import { useNextStepsReply } from "../hooks/use-next-steps-reply";
import {
  getAssistantCitationLinks,
  getAssistantDocumentCitationGroups,
  getAssistantMessageText,
  getAssistantPlanParts,
} from "../lib/ai-assistant-message";
import { parseNextStepsReplies } from "../lib/next-steps-replies";
import { useProjectNavigate } from "../routing/project-route-context";
import { AssistantDocumentCitations } from "./assistant-document-citations";
import { AssistantMessageActions } from "./assistant-message-actions";
import {
  type AssistantMessageAttachmentPart,
  AssistantMessageAttachments,
} from "./assistant-message-attachments";
import { AssistantMessageMarkdown } from "./assistant-message-markdown";
import { AssistantNextStepsReplies } from "./assistant-next-steps-replies";
import { AssistantPlanCard } from "./assistant-plan-card";
import { useClaudiaQuickReply } from "./claudia-quick-reply-context";

interface MessagePart {
  type: string;
  text?: string;
}

interface MessageLike {
  role: string;
  order: number;
  stepOrder: number;
  parts?: MessagePart[];
  text?: string;
  /** A steering message still waiting for the active run to read it. */
  deliveryState?: "queued";
}

export const QUEUED_MESSAGE_LABEL = "Queued — Claudia will pick this up in the current task";

/**
 * Where the action row sits, which is not the same answer on every device.
 *
 * On a pointer that can hover it floats: anchored to the bottom edge of the answer's prose, so
 * it is above the evidence and citation footers rather than below them — those are not what Copy
 * writes — and out of flow, so whether the flag is on decides whether a button is there to press
 * and never how much room a message takes or where the next one starts. It is painted over
 * whatever follows (`z-10`) and takes no clicks until hover or keyboard focus reveals it.
 *
 * Touch has no hover, so there the row is always visible — and a permanently visible overlay is
 * the wrong shape for it. A touch target has to be 44px, and 44px anchored below the prose
 * reaches past the citations footer's opening 28px of blank space into the next message, where
 * it would sit over that message's text and swallow taps on its chips. So on touch it is an
 * ordinary row in the flow instead, which is also what a device with neither a fine pointer nor
 * hover gets from the unprefixed classes.
 */
const messageActionsPlacement = cn(
  "mt-1 transition-opacity",
  "no-touch:absolute no-touch:left-0 no-touch:top-full no-touch:z-10",
  "no-touch:pointer-events-none no-touch:opacity-0",
  "no-touch:group-hover/message:pointer-events-auto no-touch:group-hover/message:opacity-100",
  "no-touch:group-focus-within/message:pointer-events-auto",
  "no-touch:group-focus-within/message:opacity-100"
);

function getAttachmentParts(message: MessageLike): AssistantMessageAttachmentPart[] {
  return (message.parts ?? []).filter(
    (part): part is AssistantMessageAttachmentPart => part.type === "attachment"
  );
}

/** The one rule for "this assistant message has something to show". */
export function hasRenderableAssistantContent(
  message: Pick<MessageLike, "role" | "parts" | "text">
): boolean {
  if (message.role !== "assistant") return false;
  const text = getAssistantMessageText(message).trim();
  if (text.length > 0) return true;
  if (getAssistantPlanParts(message).length > 0) return true;
  if (getAssistantDocumentCitationGroups(message).length > 0) return true;
  return getAssistantCitationLinks(message).length > 0;
}

interface AssistantMessageRowProps {
  message: MessageLike;
  compact?: boolean;
  projectId: Id<"projects">;
  threadId: string;
  /** This answer is the one whose `Next steps` options are still the options for right now. */
  interactiveNextSteps?: boolean;
}

export function AssistantMessageRow({
  message,
  compact,
  projectId,
  threadId,
  interactiveNextSteps = false,
}: AssistantMessageRowProps) {
  const navigate = useProjectNavigate();
  const quickReply = useClaudiaQuickReply();
  const { sendingLetter, sentLetter, select } = useNextStepsReply(quickReply);
  const isAssistant = message.role === "assistant";
  const text = getAssistantMessageText(message);
  // An offer nothing can take is not an offer, and an answer nobody is being asked to act on
  // must not pay for a parse of its own text on every render. The offer must also belong to this
  // row's thread: a cached transcript renders beside a composer that has moved on.
  const offersReplies =
    quickReply !== null &&
    quickReply.threadId === threadId &&
    (interactiveNextSteps || sendingLetter !== null || sentLetter !== null);
  const nextSteps = useMemo(
    () => (offersReplies ? parseNextStepsReplies(text) : null),
    [offersReplies, text]
  );
  const citations = getAssistantCitationLinks(message);
  const documentCitations = getAssistantDocumentCitationGroups(message);
  const plans = isAssistant ? getAssistantPlanParts(message) : [];
  const attachments = getAttachmentParts(message);

  if (isAssistant && !hasRenderableAssistantContent(message)) {
    return null;
  }

  if (isAssistant) {
    const bodyContent = (
      <>
        {/*
          The answer's own prose, wrapped so the action row has something to anchor to. The
          wrapper stands where the first of these three stood, and restores the column gap the
          panel's parent was giving them, so grouping them changes no spacing in either mode.
        */}
        {text.trim().length > 0 ? (
          <div className={cn("relative", compact && "flex flex-col gap-1.5")}>
            <AssistantMessageMarkdown
              content={nextSteps ? nextSteps.body : text}
              density={compact ? "compact" : "default"}
              onNavigate={navigate}
            />
            {nextSteps ? (
              <AssistantNextStepsReplies
                options={nextSteps.options}
                sendingLetter={sendingLetter}
                sentLetter={sentLetter}
                disabled={quickReply?.canSend !== true}
                onSelect={select}
              />
            ) : null}
            {nextSteps && nextSteps.tail.length > 0 ? (
              <AssistantMessageMarkdown
                className="mt-3"
                content={nextSteps.tail}
                density={compact ? "compact" : "default"}
                onNavigate={navigate}
              />
            ) : null}
            <AssistantMessageActions markdown={text} className={messageActionsPlacement} />
          </div>
        ) : null}
        {plans.map((plan) => (
          <AssistantPlanCard
            key={plan.text.slice(0, 64)}
            content={plan.text}
            className={text.trim().length > 0 ? "mt-4" : undefined}
          />
        ))}
        <AssistantDocumentCitations citations={documentCitations} />
        {citations.length > 0 ? (
          <div className="mt-4 pt-3">
            <p className="text-xs font-medium text-muted-foreground">Citations</p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {citations.map((citation) => (
                <CitationChip
                  key={`${citation.sourceId}:${citation.path}`}
                  label={citation.title ?? citation.path}
                  onClick={() => navigate(citation.path)}
                />
              ))}
            </div>
          </div>
        ) : null}
      </>
    );

    if (compact) {
      // Panel: no sparkle icon, just text body
      return (
        <article className="group/message w-full" data-message-role={message.role}>
          <div className="flex flex-col gap-1.5">{bodyContent}</div>
        </article>
      );
    }

    return (
      <article
        className="group/message flex w-full items-start gap-3"
        data-message-role={message.role}
      >
        <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center bg-surface-raised dark:bg-muted/30">
          <Sparkles className="size-3.5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">{bodyContent}</div>
      </article>
    );
  }

  return (
    <article className="flex w-full justify-end" data-message-role={message.role}>
      <div
        className={cn(
          "bg-muted",
          compact ? "max-w-full px-3 py-2" : "max-w-[75%] px-4 py-2.5 md:max-w-[75%]"
        )}
      >
        {text.trim().length > 0 ? (
          <MessageMarkdown
            content={text}
            onNavigate={navigate}
            className={cn("leading-relaxed text-foreground", compact ? "text-[13px]" : "text-sm")}
          />
        ) : null}
        <AssistantMessageAttachments
          attachments={attachments}
          compact={compact}
          projectId={projectId}
          threadId={threadId}
        />
        {message.deliveryState === "queued" ? (
          <p
            data-testid="assistant-message-queued-label"
            className="mt-1.5 flex items-center gap-1.5 text-[11px] leading-4 text-muted-foreground"
          >
            <Clock3 aria-hidden="true" className="size-3 shrink-0" />
            <span>{QUEUED_MESSAGE_LABEL}</span>
          </p>
        ) : null}
      </div>
    </article>
  );
}
